import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrgFormFields, BrgFormValues, BrgFormErrors } from '../../components/brg/BrgFormFields';
import { InactivateConfirmModal } from '../../components/brg/InactivateConfirmModal';
import { useBrgDetail } from '../../hooks/useBrgDetail';
import { useRoles } from '../../hooks/useRoles';
import { useUpdateBrg, useInactivateBrg } from '../../hooks/useBrgMutations';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ErrorMessage } from '../../components/shared/ErrorMessage';
import { ApiError, BrgDetailDto, UserDropdownDto } from '../../types';

function detailToFormValues(detail: BrgDetailDto): BrgFormValues {
  const roleAssignments: Record<number, UserDropdownDto[]> = {};
  if (detail.roleAssignments) {
    for (const a of detail.roleAssignments) {
      if (a.userId) {
        const user: UserDropdownDto = { userId: a.userId, displayName: a.userName ?? '', email: '' };
        if (!roleAssignments[a.roleId]) {
          roleAssignments[a.roleId] = [];
        }
        roleAssignments[a.roleId].push(user);
      }
    }
  }
  return {
    name: detail.name,
    description: detail.description ?? '',
    roleAssignments,
  };
}

export function EditBrgPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const detailQuery = useBrgDetail(id);
  const rolesQuery = useRoles();
  const updateMutation = useUpdateBrg(id!);
  const inactivateMutation = useInactivateBrg();

  const [values, setValues] = useState<BrgFormValues | null>(null);
  const [errors, setErrors] = useState<BrgFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showInactivateModal, setShowInactivateModal] = useState(false);

  useEffect(() => {
    if (detailQuery.data) {
      setValues(detailToFormValues(detailQuery.data));
    }
  }, [detailQuery.data]);

  if (detailQuery.isLoading || rolesQuery.isLoading || !values) {
    return <LoadingSpinner message="Loading BRG details..." fullPage />;
  }

  if (detailQuery.isError) {
    return (
      <div style={{ padding: '24px' }}>
        <ErrorMessage
          message={detailQuery.error?.message ?? 'Failed to load BRG.'}
          onRetry={() => void detailQuery.refetch()}
        />
      </div>
    );
  }

  const roles = rolesQuery.data ?? [];

  function handleFieldChange(field: 'name' | 'description', value: string) {
    setValues((prev) => prev ? { ...prev, [field]: value } : prev);
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setSubmitError(null);
  }

  function handleRoleChange(roleId: number, users: UserDropdownDto[]) {
    setValues((prev) => {
      if (!prev) return prev;
      return { ...prev, roleAssignments: { ...prev.roleAssignments, [roleId]: users } };
    });
    setErrors((prev) => {
      const newRoleErrors = { ...prev.roleErrors };
      delete newRoleErrors[roleId];
      return { ...prev, roleErrors: newRoleErrors };
    });
    setSubmitError(null);
  }

  function validate(): BrgFormErrors {
    if (!values) return {};
    const errs: BrgFormErrors = {};
    if (!values.name.trim()) errs.name = 'Please complete all mandatory fields';
    if (!values.description.trim()) errs.description = 'Please complete all mandatory fields';
    const roleErrors: Record<number, string> = {};
    for (const role of roles) {
      if (role.isRequired && (!values.roleAssignments[role.roleId] || values.roleAssignments[role.roleId].length === 0)) {
        roleErrors[role.roleId] = 'Please complete all mandatory fields';
      }
    }
    if (Object.keys(roleErrors).length > 0) errs.roleErrors = roleErrors;
    return errs;
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!values) return;

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setSubmitError(null);

    try {
      // Flatten role assignments: each user gets their own entry
      const roleAssignments = Object.entries(values.roleAssignments)
        .flatMap(([roleId, users]) => 
          (users ?? []).map(user => ({ roleId: Number(roleId), userId: user.userId }))
        );

      await updateMutation.mutateAsync({
        name: values.name.trim(),
        description: values.description.trim(),
        roleAssignments,
      });
      void navigate('/admin');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError('A BRG with this name already exists.');
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    }
  }

  async function handleInactivateConfirm() {
    if (!id) return;
    try {
      await inactivateMutation.mutateAsync(id);
      void navigate('/admin');
    } catch {
      setSubmitError('Failed to inactivate BRG. Please try again.');
      setShowInactivateModal(false);
    }
  }

  const isBusy = updateMutation.isPending || inactivateMutation.isPending;

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>

      <form onSubmit={(e) => void handleSave(e)} noValidate>
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '32px 40px',
            marginBottom: '24px',
          }}
        >
          <BrgFormFields
            values={values}
            errors={errors}
            roles={roles}
            onChange={handleFieldChange}
            onRoleChange={handleRoleChange}
            disabled={isBusy}
          />
        </div>

        {submitError && (
          <div
            role="alert"
            style={{
              color: '#c5192d',
              fontSize: '0.95rem',
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '4px',
            }}
          >
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setShowInactivateModal(true)}
            disabled={isBusy}
            style={{
              padding: '10px 28px',
              border: '1px solid #e65100',
              borderRadius: '4px',
              backgroundColor: '#fff',
              color: '#e65100',
              cursor: isBusy ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              opacity: isBusy ? 0.6 : 1,
            }}
          >
            INACTIVATE
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={() => void navigate('/admin')}
              disabled={isBusy}
              style={{
                padding: '10px 28px',
                border: '1px solid #d0d0d0',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#333',
                cursor: isBusy ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                transition: 'all 0.2s',
                opacity: isBusy ? 0.6 : 1,
              }}
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={isBusy}
              style={{
                padding: '10px 28px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: isBusy ? '#cccccc' : '#0078d4',
                color: '#fff',
                cursor: isBusy ? 'wait' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                transition: 'background-color 0.2s',
              }}
            >
              {updateMutation.isPending ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
          </div>
        </div>
      </form>

      <InactivateConfirmModal
        isOpen={showInactivateModal}
        isSubmitting={inactivateMutation.isPending}
        onConfirm={() => void handleInactivateConfirm()}
        onCancel={() => setShowInactivateModal(false)}
      />
    </div>
  );
}
